import { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, Trash2, Edit2, Download, UserCog, CalendarCheck, Wallet, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function Staff() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setEmployees(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Module 07</div>
        <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{isAdmin ? "Attendance & Payroll" : "Attendance"}</h1>
        <p className="text-sm text-[#475569] mt-1">{isAdmin ? "Manage staff, mark daily attendance, and auto-calculate monthly salaries." : "Mark daily staff attendance."}</p>
      </div>

      <Tabs defaultValue="attendance">
        <TabsList data-testid="staff-tabs">
          {isAdmin && <TabsTrigger value="employees" data-testid="tab-employees"><UserCog className="w-4 h-4 mr-1.5" /> Employees</TabsTrigger>}
          <TabsTrigger value="attendance" data-testid="tab-attendance"><CalendarCheck className="w-4 h-4 mr-1.5" /> Timesheet</TabsTrigger>
          {isAdmin && <TabsTrigger value="payroll" data-testid="tab-payroll"><Wallet className="w-4 h-4 mr-1.5" /> Payroll</TabsTrigger>}
        </TabsList>

        {isAdmin && (
          <TabsContent value="employees" className="mt-4">
            <EmployeesTab employees={employees} loading={loading} refresh={fetchEmployees} setEmployees={setEmployees} />
          </TabsContent>
        )}
        <TabsContent value="attendance" className="mt-4">
          <AttendanceTab employees={employees.filter((e) => e.active)} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="payroll" className="mt-4">
            <PayrollTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ---------------- Employees ---------------- */
const emptyEmp = { name: "", mobile: "", base_salary: "", active: true };

function EmployeesTab({ employees, loading, setEmployees }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyEmp);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openCreate = () => { setEditId(null); setForm(emptyEmp); setOpen(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({ name: e.name, mobile: e.mobile, base_salary: String(e.base_salary), active: e.active });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim()) { toast.error("Name and mobile are required"); return; }
    const sal = parseFloat(form.base_salary);
    if (isNaN(sal) || sal < 0) { toast.error("Enter a valid base salary"); return; }
    setSaving(true);
    const payload = { name: form.name.trim(), mobile: form.mobile.trim(), base_salary: sal, active: form.active };
    try {
      if (editId) {
        const { data } = await api.put(`/employees/${editId}`, payload);
        setEmployees((p) => p.map((x) => (x.id === editId ? data : x)));
        toast.success("Employee updated");
      } else {
        const { data } = await api.post("/employees", payload);
        setEmployees((p) => [data, ...p]);
        toast.success("Employee added");
      }
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (emp) => {
    const prev = employees;
    setEmployees((p) => p.map((x) => (x.id === emp.id ? { ...x, active: !x.active } : x)));
    try { await api.put(`/employees/${emp.id}`, { active: !emp.active }); }
    catch (e) { setEmployees(prev); toast.error(formatApiError(e)); }
  };

  const confirmDelete = async () => {
    const id = deleteId; const prev = employees;
    setEmployees((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try { await api.delete(`/employees/${id}`); toast.success("Employee removed"); }
    catch (e) { setEmployees(prev); toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-employee-btn">
          <Plus className="w-4 h-4" /> Add Employee
        </Button>
      </div>
      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Name</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Mobile</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Base Salary</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Active</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
            ) : employees.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-[#64748B]" data-testid="employees-empty">No employees yet. Add your first staff member.</TableCell></TableRow>
            ) : employees.map((e) => (
              <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                <TableCell className="text-sm font-medium">{e.name}</TableCell>
                <TableCell className="font-mono text-xs">{e.mobile}</TableCell>
                <TableCell className="text-right font-mono">{inr(e.base_salary)}</TableCell>
                <TableCell>
                  <Switch checked={e.active} onCheckedChange={() => toggleActive(e)} data-testid={`employee-active-${e.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(e.id)} data-testid={`delete-employee-${e.id}`}><Trash2 className="w-4 h-4 text-[#EF4444]" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editId ? "Edit employee" : "Add employee"}</DialogTitle>
            <DialogDescription>Basic staff details used for attendance and payroll.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Mobile *</Label>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="font-mono" data-testid="employee-mobile" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Base Salary (₹ / month) *</Label>
              <Input type="number" min="0" step="0.01" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} className="font-mono" placeholder="e.g. 18000" data-testid="employee-salary" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="employee-active-toggle" />
              <Label className="text-sm">Active (include in attendance &amp; payroll)</Label>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="employee-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="employee-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Save changes" : "Add employee")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove employee?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the employee along with their attendance and payroll records.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-employee-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-employee-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- Attendance ---------------- */
function AttendanceTab({ employees }) {
  const [month, setMonth] = useState(thisMonth());
  const [map, setMap] = useState({}); // employee_id -> { date: status }
  const [loading, setLoading] = useState(false);

  const daysInMonth = (() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m, 0).getDate();
  })();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const dstr = (d) => `${month}-${String(d).padStart(2, "0")}`;

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/month", { params: { month } });
      const m = {};
      (data || []).forEach((r) => { (m[r.employee_id] = m[r.employee_id] || {})[r.date] = r.status; });
      setMap(m);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchMonth(); }, [fetchMonth]);

  const ORDER = [null, "Present", "Half Day", "Absent"];
  const LETTER = { "Present": "P", "Half Day": "H", "Absent": "A" };

  const cycle = async (eid, d) => {
    const date = dstr(d);
    const cur = map[eid]?.[date] ?? null;
    const next = ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length];
    const prev = map;
    setMap((mm) => {
      const copy = { ...mm, [eid]: { ...(mm[eid] || {}) } };
      if (next) copy[eid][date] = next; else delete copy[eid][date];
      return copy;
    });
    try {
      if (next) await api.put("/attendance", { employee_id: eid, date, status: next });
      else await api.delete(`/attendance/${eid}/${date}`);
    } catch (e) { setMap(prev); toast.error(formatApiError(e)); }
  };

  const fillPresent = async (eid) => {
    const prev = map;
    setMap((mm) => ({ ...mm, [eid]: Object.fromEntries(days.map((d) => [dstr(d), "Present"])) }));
    try {
      await api.post("/attendance/bulk", { entries: days.map((d) => ({ employee_id: eid, date: dstr(d), status: "Present" })) });
      toast.success("Marked all days Present");
    } catch (e) { setMap(prev); toast.error(formatApiError(e)); }
  };

  const cellStyle = (s) =>
    s === "Present" ? "bg-[#DCFCE7] text-[#166534]"
      : s === "Half Day" ? "bg-[#FEF3C7] text-[#92400E]"
      : s === "Absent" ? "bg-[#FEE2E2] text-[#991B1B]"
      : "bg-white text-[#CBD5E1]";

  const empTotals = (eid) => {
    const vals = Object.values(map[eid] || {});
    return {
      p: vals.filter((v) => v === "Present").length,
      h: vals.filter((v) => v === "Half Day").length,
      a: vals.filter((v) => v === "Absent").length,
    };
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-[#475569]">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="sm:w-48" data-testid="attendance-month" />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-[#475569]">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#DCFCE7] border border-[#22C55E]" /> Present</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FEF3C7] border border-[#F59E0B]" /> Half</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FEE2E2] border border-[#EF4444]" /> Absent</span>
          <span className="text-[#64748B]">· tap a cell to cycle P→H→A→clear</span>
        </div>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        {loading ? (
          <div className="text-center py-10 text-sm text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
        ) : employees.length === 0 ? (
          <div className="text-center py-10 text-sm text-[#64748B]" data-testid="attendance-empty">No active employees. Add staff in the Employees tab.</div>
        ) : (
          <table className="border-collapse text-xs w-full" data-testid="attendance-grid">
            <thead>
              <tr className="bg-[#F8F9FA]">
                <th className="sticky left-0 bg-[#F8F9FA] z-10 px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider min-w-[110px]">Employee</th>
                {days.map((d) => <th key={d} className="px-0.5 py-2 font-mono text-[10px] text-[#64748B] text-center">{d}</th>)}
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-[#64748B] text-center whitespace-nowrap">P/H/A</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => {
                const t = empTotals(e.id);
                return (
                  <tr key={e.id} data-testid={`att-row-${e.id}`} className="border-t border-[#F1F5F9]">
                    <td className="sticky left-0 bg-white z-10 px-3 py-1.5 font-medium whitespace-nowrap">{e.name}</td>
                    {days.map((d) => {
                      const s = map[e.id]?.[dstr(d)] ?? null;
                      return (
                        <td key={d} className="p-0.5 text-center">
                          <button type="button" onClick={() => cycle(e.id, d)} className={`w-full min-w-[24px] max-w-[34px] h-7 mx-auto rounded text-[11px] font-medium border border-[#E2E8F0] transition-colors ${cellStyle(s)}`} data-testid={`att-cell-${e.id}-${d}`}>
                            {LETTER[s] || ""}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-2 text-center font-mono whitespace-nowrap"><span className="text-[#166534]">{t.p}</span>/<span className="text-[#92400E]">{t.h}</span>/<span className="text-[#991B1B]">{t.a}</span></td>
                    <td className="px-2"><Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => fillPresent(e.id)} data-testid={`att-fill-${e.id}`}>All P</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- Payroll ---------------- */
function PayrollTab() {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll", { params: { month } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  const persist = async (employeeId, patch) => {
    try {
      const { data } = await api.put("/payroll", { employee_id: employeeId, month, ...patch });
      setRows((p) => p.map((r) => (r.employee_id === employeeId ? { ...r, ...data } : r)));
    } catch (e) { toast.error(formatApiError(e)); fetchPayroll(); }
  };

  const onExceptionChange = (employeeId, val) => {
    const n = val === "" ? 0 : Math.max(0, parseInt(val, 10) || 0);
    setRows((p) => p.map((r) => (r.employee_id === employeeId ? { ...r, exception_days: n } : r)));
  };

  const toggleStatus = (row) => {
    const next = row.status === "Paid" ? "Pending" : "Paid";
    persist(row.employee_id, { status: next, exception_days: row.exception_days });
    setRows((p) => p.map((r) => (r.employee_id === row.employee_id ? { ...r, status: next } : r)));
  };

  const handleExport = () => {
    const token = localStorage.getItem("access_token");
    fetch(`${API}/exports/payroll.csv?month=${month}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `payroll-${month}.csv`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  const totalPayable = rows.reduce((s, r) => s + Number(r.salary || 0), 0);
  const totalPaid = rows.filter((r) => r.status === "Paid").reduce((s, r) => s + Number(r.salary || 0), 0);
  const totalPending = totalPayable - totalPaid;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-[#475569]">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="sm:w-48" data-testid="payroll-month" />
        </div>
        <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-payroll-btn">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-white border border-[#E2E8F0] rounded-lg" data-testid="payroll-total">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Total Payroll</div>
          <div className="text-lg font-display font-semibold mt-0.5">{inr(totalPayable)}</div>
        </div>
        <div className="p-3 bg-white border border-[#E2E8F0] rounded-lg" data-testid="payroll-paid">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Paid</div>
          <div className="text-lg font-display font-semibold mt-0.5 text-[#22C55E]">{inr(totalPaid)}</div>
        </div>
        <div className="p-3 bg-white border border-[#E2E8F0] rounded-lg" data-testid="payroll-pending">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Pending</div>
          <div className="text-lg font-display font-semibold mt-0.5 text-[#F59E0B]">{inr(totalPending)}</div>
        </div>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Employee</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Base</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-center">P / H / A</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-center">Leave Exception</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Payable Days</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Salary</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-[#64748B]" data-testid="payroll-empty">No active employees to process.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.employee_id} data-testid={`payroll-row-${r.employee_id}`}>
                <TableCell className="text-sm font-medium">{r.name}</TableCell>
                <TableCell className="text-right font-mono text-xs">{inr(r.base_salary)}</TableCell>
                <TableCell className="text-center font-mono text-xs">
                  <span className="text-[#166534]">{r.present_days}</span> / <span className="text-[#92400E]">{r.half_days}</span> / <span className="text-[#991B1B]">{r.absent_days}</span>
                </TableCell>
                <TableCell className="text-center">
                  <Input
                    type="number" min="0"
                    value={r.exception_days}
                    onChange={(e) => onExceptionChange(r.employee_id, e.target.value)}
                    onBlur={(e) => persist(r.employee_id, { exception_days: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    className="w-16 h-8 text-center font-mono mx-auto"
                    data-testid={`payroll-exception-${r.employee_id}`}
                  />
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{r.payable_days}</TableCell>
                <TableCell className="text-right font-mono font-semibold" data-testid={`payroll-salary-${r.employee_id}`}>{inr(r.salary)}</TableCell>
                <TableCell className="text-center">
                  <button
                    type="button"
                    onClick={() => toggleStatus(r)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${r.status === "Paid" ? "bg-[#DCFCE7] text-[#166534] border-[#22C55E]" : "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]"}`}
                    data-testid={`payroll-status-${r.employee_id}`}
                    title="Click to toggle Paid / Pending"
                  >
                    {r.status === "Paid" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                    {r.status}
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-[#64748B]">Salary = (Base ÷ 30) × (Present + ½·Half-day + exempted absents). Enter Leave Exception to pay absent days as present.</p>
    </div>
  );
}
